import {getImageUrl, getVideoUrl, Sizes} from '@cohort/shared/utils/media';
import {classNames} from '@cohort/shared-frontend/utils/classNames';
import {Spinner} from '@cohort/wallet/components/Spinner';
import TrackableInternalLink from '@cohort/wallet/components/tracking/TrackableInternalLink';
import useSpaceContext from '@cohort/wallet/hooks/useSpaceContext';
import useThemeContext from '@cohort/wallet/hooks/useThemeContext';
import {getNftDetailRoute} from '@cohort/wallet/lib/Pages';
import {Colors} from '@cohort/wallet/lib/Theme';
import type {OwnershipWDto} from '@cohort/wallet-schemas/ownership';
import {CaretRight} from '@phosphor-icons/react';
import React, {Fragment, useCallback} from 'react';
import {useTranslation} from 'react-i18next';

type DigitalAssetCardProps = {
  name: string;
  ownership?: OwnershipWDto;
  imageFileKey: string;
  animationFileKey?: string;
  transfering?: boolean;
  tokenId: number;
};

export const DigitalAssetCard: React.FC<DigitalAssetCardProps> = ({
  name,
  ownership,
  imageFileKey,
  transfering,
  animationFileKey,
  tokenId,
}) => {
  const {space, setTransitioning} = useSpaceContext();
  const {hasDarkBg, accentColor} = useThemeContext();
  const {t} = useTranslation('components', {keyPrefix: 'space.digitalAssetCard'});

  const handleCardClick = useCallback(() => {
    if (space === undefined || ownership === undefined) {
      return;
    }
    setTransitioning({active: true, enterFrom: 'left'});
  }, [ownership, setTransitioning, space]);

  return (
    <TrackableInternalLink
      className={classNames(
        'grow',
        (transfering || ownership === undefined) && 'pointer-events-none'
      )}
      to={getNftDetailRoute(ownership?.id ?? '')}
      onClick={handleCardClick}
      tracking={{namespace: 'digitalAssets.list.card', metadata: {ownershipId: ownership?.id}}}
    >
      <div key={name} className="flex h-full flex-col">
        <div
          data-testid="digital-asset-visual"
          className={classNames(
            'group relative overflow-hidden rounded-t-xl px-4 pt-4',
            hasDarkBg ? 'bg-white/8' : 'bg-black/4'
          )}
        >
          <div
            className={classNames(
              'pointer-events-none absolute flex h-full w-full items-center justify-center overflow-hidden rounded-lg bg-black/50 backdrop-blur-sm transition duration-500 ease-in-out',
              transfering ? 'opacity-100' : 'hidden opacity-0'
            )}
          >
            <Spinner color={Colors.White} />
          </div>

          {animationFileKey === undefined ? (
            <img
              src={getImageUrl(import.meta.env.COHORT_ENV, imageFileKey, {h: Sizes.M, w: Sizes.M})}
              alt={name}
              className="rounded-lg object-cover object-center"
            />
          ) : (
            <video
              controls={false}
              disablePictureInPicture
              disableRemotePlayback
              autoPlay
              playsInline
              webkit-playsinline="true"
              loop
              muted
              className="rounded-lg object-cover object-center"
            >
              <source
                src={getVideoUrl(import.meta.env.COHORT_ENV, animationFileKey, {
                  h: Sizes.M,
                  w: Sizes.M,
                })}
              />
              <img
                src={getImageUrl(import.meta.env.COHORT_ENV, imageFileKey, {
                  h: Sizes.M,
                  w: Sizes.M,
                })}
                alt={name}
                className="rounded-lg object-cover object-center"
              />
            </video>
          )}
        </div>

        <div
          className={classNames(
            'flex grow justify-between gap-4 rounded-b-lg p-4',
            hasDarkBg ? 'bg-white/8 text-white' : 'bg-black/4 text-black/90'
          )}
        >
          <div className="flex flex-col">
            <h3 className="text-lg font-medium leading-6">{name}</h3>
            {transfering ? (
              <div
                className={classNames(
                  'mt-4 text-sm',
                  hasDarkBg ? 'text-white/60' : 'text-black/60'
                )}
              >
                {t('pendingTitle')}
              </div>
            ) : (
              <Fragment>
                <div
                  className={classNames(
                    'mt-2 text-sm',
                    hasDarkBg ? 'text-white/60' : 'text-black/60'
                  )}
                >
                  ID #{tokenId}
                </div>
                <div
                  className={classNames(
                    'mt-2 text-sm',
                    hasDarkBg ? 'text-white/60' : 'text-black/60'
                  )}
                >
                  {ownership?.createdAt.toLocaleDateString()}
                </div>
              </Fragment>
            )}
          </div>
          <div>
            <CaretRight size={24} style={{color: accentColor}} />
          </div>
        </div>
      </div>
    </TrackableInternalLink>
  );
};
